// RequireAuth.jsx
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

const SESSION_KEY = "__cms_session";
export const sideBars = "__sideBars__";
export const boardMasters = "__boardMasters__";

export function readSession(typ) {
    try { 
        return JSON.parse(window.sessionStorage.getItem(typ)); 
    }
    catch(e) {
        console.error(e); 
        return null; 
    }
}

export function saveSession(typ, data) {
    try {
        window.sessionStorage.setItem(typ, JSON.stringify(data));
    } catch(e) {
        console.error(e);
    }
}

// 유효성 검사: token 있고, 절대만료(abs)·유휴만료(exp) 모두 now 이후여야 함
// 유휴만료 = 활동없으면 종료(30분), 절대만료 = 하루 작업(12시간)
// 만료 2분전 경고 모달 + 활동 시 연장(슬라이딩)
function isValid(s) {
    if (!s || !s.token) return false;
        const now = Date.now();
    if (s.abs && Number(s.abs) <= now) return false;
    if (!s.exp || Number(s.exp) <= now) return false;
    return true;
}

// 남은 시간(ms) 계산 (다음 체크 시점: exp과 abs 중 더 이른 것)
function remainMs(s) {
    if (!s) return 0;
        const now = Date.now();
        const exps = [Number(s.exp)];
    if (s.abs) exps.push(Number(s.abs));
        const next = Math.min(...exps);
    return Math.max(0, next - now);
}

/**
 * 보호 라우트
 * - 세션 없음 → redirect (기본: /cms/login)
 * - 만료(유휴/절대) → redirectIfExpired (기본: /)
 * - idleExtendMs: 사용자 활동 시 exp를 now + idleExtendMs 로 연장(단, abs는 넘지 않음)
 */
export default function RequireAuth({
    children,
    redirect = "/cms/login",     // 토큰 없을 때
    redirectIfExpired = "/",     // 만료됐을 때(홈으로)
    idleExtendMs = null, // 예: 30 * 60 * 1000  (없으면 연장 안 함)
}) {
    const loc = useLocation();
    const [ok, setOk] = useState(() => isValid(readSession()));
    const [tick, setTick] = useState(0); // 타임아웃 재설정 트리거
    const timerRef = useRef(null);
    const lastTouchRef = useRef(0);

    // 만료 여부 재확인 + 상태 반영
    const check = () => setOk(isValid(readSession()));

    // 활동 시 유휴 연장 (슬라이딩 세션)
    const touch = () => {
        if (!idleExtendMs) return;
        const now = Date.now();
        // 너무 잦은 저장 방지(최소 5초 간격)
        if (now - lastTouchRef.current < 5000) return;
        lastTouchRef.current = now;

        const s= readSession();
        if (!s || !s.token) return;
        if (s.abs && Number(s.abs) <= now) return; // 절대만료 지났으면 연장 금지
        
        const nextExp = now + idleExtendMs;
        s.exp = s.abs ? Math.min(nextExp, Number(s.abs)) : nextExp;
        saveSession(s);

        // 유효성/타이머 즉시 갱신
        setOk(isValid(s));
        setTick((t) => t + 1);
    };

    // 라우트 변경./세션 변경 시: 즉시 검사 + 다음 만료 시각에 맞춰 타이머 1회 설정
    useEffect(() => {
        // 정리
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // 즉시 1회 확인
        check();

        //세션 만료시 홈페이지로 돌아가는 테스트용 코드
        //const t = setInterval(check, 1000); // 1초 간격으로 머무르는 도중 만료 체크

        // 다음 만료 시점에만 한 번 깨어남
        const s = readSession();
        const ms = remainMs(s);
        if (ms > 0) {
        timerRef.current = setTimeout(() => {
            check();
            // 만료 직후 네비게이션 일으키기 위해 tick 증가
            setTick((t) => t + 1);
        }, ms + 50); // 약간의 버퍼
        }

        return () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        };
    }, [loc, tick]);

    // 다른 탭/창에서 세션 변경(storage) 감지 → 즉시 재설정
    useEffect(() => {
        const onStorage = (e) => {
        if (e.key === SESSION_KEY) {
            check();
            setTick((t) => t + 1);
        }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    // (옵션) 사용자 활동 이벤트로 유휴 연장
    useEffect(() => {
        if (!idleExtendMs) return;
        const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
        events.forEach((evt) => window.addEventListener(evt, touch, { passive: true }));
        return () => events.forEach((evt) => window.removeEventListener(evt, touch));
    }, [idleExtendMs]);

    if (!ok) {
        const s = readSession();
        return (
        <Navigate
            to={s?.token ? redirectIfExpired : redirect}
            replace
            state={{ from: loc }}
        />
        );
    }
    return children;
}
